import React, { useState, Fragment, useEffect, memo } from "react";
import Loader from "components/Progress";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import Card from "@mui/material/Card";
import { useSelector, useDispatch } from "react-redux";
import Chart from "components/Charts/Donut";
import Nodata from "assets/img/pieChart.png";
import { useGetAlarmsQuery } from "../../../services/alarms";
import { alarmsQuery } from "../List";

function Count({
  type,
  setAllAlarms,
  generateRows,
  generateExportData,
  serviceId
}) {
  const metaDataValue = useSelector((state) => state.metaData);
  const [totalDocuments,setTotalDocuments]=useState(null)
  const [totalRows,setTotalRows]=useState([])
  let priorities = ["CRITICAL", "MAJOR", "MINOR", "WARNING"];
  let statuses = ["ACTIVE", "ACKNOWLEDGED", "CLEARED"];
  const [legends, setLegends] = useState({ priorities: [], status: [] });
  const [alarmsCount, setAlarmsCount] = useState({
    priorities: [],
    status: [],
  });
  const alarmsFilter = useSelector((state) => state.alarmsFilter);
  const filterParams = alarmsQuery(alarmsFilter, {
    serviceId,
    services: metaDataValue.services,
  });
  const alarmsRes = useGetAlarmsQuery({
    token: window.localStorage.getItem("token"),
    params: `?${filterParams}&pageSize=10&withTotalPages=true&currentPage=1`,
  });
  // First fetch runs on every filter change (RTK Query keys on the params
  // string). Previously `skip: totalDocuments != null` made this fire exactly
  // once per component lifetime — so changing filters left `totalDocuments`
  // frozen at the initial query's value, and `allAlarmsRes` (which uses it
  // as pageSize) then fetched a stale-sized window of the new filter result.
  // Cross-filter widget breakdowns drifted as a result.


  // allalarms keeps this read's cache entry apart from the first read's. With 0 or
  // 10 alarms the two requests would otherwise be identical: this read's fetch shows
  // as the first read fetching, which skips this read, and its return fetches again,
  // without end (the server ignores the parameter; the List's full read sends it too).
  const allAlarmsRes = useGetAlarmsQuery(
    {
    token: window.localStorage.getItem("token"),
    params: `?allalarms=true&${filterParams}&pageSize=${totalDocuments==0?10:totalDocuments}&withTotalPages=true&currentPage=1`,
  },
  {skip: alarmsRes.isFetching || totalDocuments==null}//conditioanl fetch
  );

  function getCountsBy(type, count) {
    let tempCounts = [];
    for (let a in count) {
      tempCounts.push({
        value: count[a],
        category: a[0] + a.toLowerCase().slice(1),
      });
    }
    (type == "priorities" ? priorities : statuses).forEach((s) => {
      if (!count[s]) {
        count[s] = 0;
      }
    });
    return tempCounts;
  }

  useEffect(() => {
    if (!alarmsRes.isFetching && alarmsRes.isSuccess ) {
      // console.log(alarmsRes.data.payload.totalDocuments)
      // an empty result carries no totalDocuments: 0, so the full read still
      // runs and clears the charts
      setTotalDocuments(alarmsRes.data.payload.totalDocuments || 0)
    }
  }, [alarmsRes.isFetching]);

  useEffect(()=>{
    if (!allAlarmsRes.isFetching && allAlarmsRes.isSuccess) {
      let data = allAlarmsRes.data.payload.data;
      // console.log("data",data)
      let exportData;
      if (data ) {
        exportData = generateRows(data);
        // an empty result clears the CSV (no export of the previous filter's alarms)
        generateExportData(exportData && exportData.length ? exportData : null);
        let tempPriorities = {};
        let tempStatus = {};
        let total = 0;
        let tempLegends = {};
        setAllAlarms(data);
        data.forEach((d) => {
          tempPriorities[d.severity] = tempPriorities[d.severity]
            ? tempPriorities[d.severity] + 1
            : 1;
          tempStatus[d.status] = tempStatus[d.status]
            ? tempStatus[d.status] + 1
            : 1;
          total = total + 1;
        });
        Object.keys(tempPriorities).forEach((t) => {
          if (tempPriorities[t] == 0) {
            delete tempPriorities[t];
          }
        });
        Object.keys(tempStatus).forEach((t) => {
          if (tempStatus[t] == 0) {
            delete tempStatus[t];
          }
        });

        setAlarmsCount({
          priorities: getCountsBy("priorities", tempPriorities),
          status: getCountsBy("status", tempStatus),
        });
        tempLegends = {
          priorities: { ...tempPriorities, TOTAL: total },
          status: { ...tempStatus, TOTAL: total },
        };
        setLegends(tempLegends);
      } else {
        generateExportData(null);
        setAllAlarms([]);
        setLegends({ priorities: [], status: [] });
        setAlarmsCount({ priorities: [], status: [] });
      }
    } else if (!allAlarmsRes.isFetching && allAlarmsRes.isError) {
      // e.g. more than 2,000 matching alarms (the server caps pageSize at
      // 2000 and answers 400): no previous filter's counts or CSV remain
      // (code review 2026-10-30)
      generateExportData(null);
      setAllAlarms([]);
      setLegends({ priorities: [], status: [] });
      setAlarmsCount({ priorities: [], status: [] });
    }
  },[allAlarmsRes.isFetching])

  function getLegendsByChart() {
    let priorities = [
      {
        name: "CRITICAL",
        color: "#bf3535",
        fade: "rgb(191,53,53,0.1)",
      },
      {
        name: "MAJOR",
        color: "#844204",
        fade: "rgb(132,66,4,0.1)",
      },
      {
        name: "MINOR",
        color: "#fe9f1b",
        fade: "rgb(254,160,60,0.1)",
      },
      {
        name: "WARNING",
        color: "#3399ff",
        fade: "rgb(66,161,255,0.1)",
      },
    ];
    let statuses = [
      {
        name: "CLEARED",
        color: "#bf3535",
        fade: "rgb(191,53,53,0.1)",
      },
      {
        name: "ACKNOWLEDGED",
        color: "#3399ff",
        fade: "rgb(66,161,255,0.1)",
      },
      {
        name: "ACTIVE",
        color: "#5fb762",
        fade: "rgb(95,183,98,0.1)",
      },
    ];
    return (type == "priorities" ? priorities : statuses).map((elm) => (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "3.5px",
          backgroundColor: elm.fade,
          padding: "5px 15px",
          borderRadius: "10px",
        }}
      >
        <span
          style={{ color: elm.color, fontWeight: "bold", fontSize: "11px" }}
        >
          {elm.name}
        </span>
        <p style={{ fontSize: "11px", color: elm.color }}>
          <b>
            {type == "priorities" && elm.name == "HEALTHY"
              ? `${legends[type][elm.name]} `
              : legends[type][elm.name]}
          </b>
        </p>
      </div>
    ));
  }

  return (
    <Card
      style={{
        // shorter (2026-10-30, owner): more room for the alarm list
        maxHeight: "215px",
        minHeight: "215px",
        maxWidth: "255px",
        minWidth: "255px",
        margin: "10px 16px 16px 0px",
      }}
    >
      <div>
        <span
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "8px 10px 0px 10px",
          }}
        >
          <p
            style={{
              color: "#bfbec8",
              fontSize: "15px",
              textTransform: "capitalize",
              margin: "4px 0px",
            }}
          >
            <b>{`${type == "priorities" ? "Priority" : type}`}</b>
          </p>
          <NotificationsActiveOutlinedIcon style={{ color: "#bfbec8" }} />
        </span>
        <div
          style={{
            height: "170px",
            width: "100%",
          }}
        >
          {alarmsRes.isFetching || allAlarmsRes.isFetching ? (
            <span
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Loader />
            </span>
          ) : (
            <Fragment>
              {allAlarmsRes.isError ? (
                <p
                  style={{
                    color: "#9f9f9f",
                    fontSize: "13px",
                    padding: "10px",
                  }}
                >
                  {totalDocuments > 2000
                    ? "Too many alarms to count here: narrow the filters."
                    : "Could not count the alarms."}
                </p>
              ) : alarmsCount[type].length ? (
                <div>
                  <Chart
                    name={type == "priorities" ? "priority" : type}
                    data={alarmsCount[type]}
                    boxHeight="100px"
                    radius={42}
                  />
                  <div
                    style={{
                      display: "flex",
                      height: "60px",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "6px 10px 4px 10px",
                    }}
                  >
                    {/* the shared .grid style lifts the chips 10 px (bottom: 10px), onto
                        the donut once the card got shorter; an inline style cannot take
                        "!important", so the old value never applied (2026-10-30) */}
                    <div className="grid" style={{ bottom: 0 }}>
                      {getLegendsByChart()}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    height: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                    color: "#c8c8c8",
                  }}
                >
                  <img src={Nodata} height="100px" width="100px" />
                  <p>No Alarms Found</p>
                </div>
              )}
            </Fragment>
          )}
        </div>
      </div>
    </Card>
  );
}
export default memo(Count);
